import { useEffect } from 'react'

const SITE = 'https://www.websiteforge.com'

/**
 * TODO (content): replace with an approved absolute 1200x630 social card image.
 * The logo file is too small to serve as the large social image.
 */
const SOCIAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/sfio5nMTtdMQFgmLKeBBxC/Logo_(1)_1.png'

export type PageMeta = {
  title: string
  description: string
  /** Path relative to the site root, e.g. '/portfolio'. */
  path: string
}

export const pageMeta: Record<'home' | 'portfolio' | 'pricing' | 'notFound', PageMeta> = {
  home: {
    title: 'Custom Websites, Ecommerce, SEO & Web Systems | WebsiteForge',
    description:
      'WebsiteForge designs, builds, and manages custom websites, ecommerce, SEO, lead systems, integrations, hosting, and ongoing improvements for businesses.',
    path: '/',
  },
  portfolio: {
    title: 'Custom Website & Ecommerce Portfolio | WebsiteForge',
    description:
      'Selected WebsiteForge client work across ecommerce, service, membership, and custom-functionality projects, each shaped around different operating requirements.',
    path: '/portfolio',
  },
  pricing: {
    title: 'Custom Website & Ecommerce Project Pricing | WebsiteForge',
    description:
      'How WebsiteForge scopes and prices custom website, ecommerce, and ongoing support engagements, and what affects the scope of a project.',
    path: '/pricing',
  },
  notFound: {
    title: 'Page Not Found | WebsiteForge',
    description: 'The page you requested could not be found on WebsiteForge.',
    path: '/404',
  },
}

function upsert(selector: string, create: () => HTMLElement) {
  let el = document.head.querySelector(selector) as HTMLElement | null
  if (!el) {
    el = create()
    document.head.appendChild(el)
  }
  return el
}

function setMetaByName(name: string, content: string) {
  const el = upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute('name', name)
    return m
  })
  el.setAttribute('content', content)
}

function setMetaByProperty(property: string, content: string) {
  const el = upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute('property', property)
    return m
  })
  el.setAttribute('content', content)
}

/** Applies per-route title, description, canonical, and social metadata. */
export function useDocumentMeta(meta: PageMeta) {
  useEffect(() => {
    const url = `${SITE}${meta.path}`

    document.title = meta.title

    setMetaByName('description', meta.description)
    setMetaByName('twitter:card', 'summary_large_image')
    setMetaByName('twitter:title', meta.title)
    setMetaByName('twitter:description', meta.description)
    setMetaByName('twitter:image', SOCIAL_IMAGE)

    setMetaByProperty('og:type', 'website')
    setMetaByProperty('og:site_name', 'WebsiteForge')
    setMetaByProperty('og:title', meta.title)
    setMetaByProperty('og:description', meta.description)
    setMetaByProperty('og:url', url)
    setMetaByProperty('og:image', SOCIAL_IMAGE)

    const canonical = upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link')
      l.setAttribute('rel', 'canonical')
      return l
    })
    canonical.setAttribute('href', url)

    const icon = upsert('link[rel="icon"]', () => {
      const l = document.createElement('link')
      l.setAttribute('rel', 'icon')
      return l
    })
    icon.setAttribute('type', 'image/png')
    icon.setAttribute('href', SOCIAL_IMAGE)
  }, [meta])
}
